import React from 'react';
import { Thermometer, Droplets, Zap, Sun, Users } from 'lucide-react';

export interface SensorDisplayStyle03Props {
  type: 'temperature' | 'humidity' | 'power' | 'lux' | 'occupancy';
  value?: number | boolean;
  min?: number;
  max?: number;
  unit?: string;
  label?: string;
}

const SENSOR_CONFIG: Record<string, {
  icon: React.ElementType;
  color: string;
  gradientFrom: string;
  gradientTo: string;
  unit: string;
  label: string;
  defaultMin: number;
  defaultMax: number;
  formatValue: (v: any) => string;
}> = {
  temperature: {
    icon: Thermometer,
    color: 'text-orange-400',
    gradientFrom: 'from-blue-500',
    gradientTo: 'to-orange-500',
    unit: '°C',
    label: '溫度',
    defaultMin: 0,
    defaultMax: 40,
    formatValue: (v) => typeof v === 'number' ? v.toFixed(1) : '--'
  },
  humidity: {
    icon: Droplets,
    color: 'text-blue-400',
    gradientFrom: 'from-yellow-500',
    gradientTo: 'to-blue-500',
    unit: '%',
    label: '濕度',
    defaultMin: 0,
    defaultMax: 100,
    formatValue: (v) => typeof v === 'number' ? v.toFixed(0) : '--'
  },
  power: {
    icon: Zap,
    color: 'text-yellow-400',
    gradientFrom: 'from-green-500',
    gradientTo: 'to-red-500',
    unit: 'W',
    label: '功率',
    defaultMin: 0,
    defaultMax: 3000,
    formatValue: (v) => {
      if (typeof v !== 'number') return '--';
      return v >= 1000 ? `${(v / 1000).toFixed(2)}kW` : `${v.toFixed(0)}W`;
    }
  },
  voltage: {
    icon: Zap,
    color: 'text-yellow-300',
    gradientFrom: 'from-yellow-600',
    gradientTo: 'to-yellow-300',
    unit: 'V',
    label: '電壓',
    defaultMin: 200,
    defaultMax: 250,
    formatValue: (v) => typeof v === 'number' ? v.toFixed(1) : '--'
  },
  current: {
    icon: Zap,
    color: 'text-orange-300',
    gradientFrom: 'from-green-500',
    gradientTo: 'to-orange-500',
    unit: 'A',
    label: '電流',
    defaultMin: 0,
    defaultMax: 20,
    formatValue: (v) => typeof v === 'number' ? v.toFixed(2) : '--'
  },
  energy: {
    icon: Zap,
    color: 'text-green-400',
    gradientFrom: 'from-green-600',
    gradientTo: 'to-green-300',
    unit: 'kWh',
    label: '累積用電',
    defaultMin: 0,
    defaultMax: 100,
    formatValue: (v) => typeof v === 'number' ? v.toFixed(2) : '--'
  },
  lux: {
    icon: Sun,
    color: 'text-amber-400',
    gradientFrom: 'from-slate-600',
    gradientTo: 'to-amber-400',
    unit: 'lux',
    label: '光照',
    defaultMin: 0,
    defaultMax: 1000,
    formatValue: (v) => typeof v === 'number' ? v.toFixed(0) : '--'
  },
  occupancy: {
    icon: Users,
    color: 'text-purple-400',
    gradientFrom: 'from-purple-600',
    gradientTo: 'to-purple-400',
    unit: '',
    label: '狀態',
    defaultMin: 0,
    defaultMax: 1,
    formatValue: (v) => v ? '有人' : '無人'
  }
};

/**
 * SensorDisplay Style 03 - Gauge
 * Circular gauge with gradient arc indicator
 */
export const Style03_Gauge: React.FC<SensorDisplayStyle03Props> = ({
  type,
  value,
  min,
  max,
  unit,
  label
}) => {
  const config = SENSOR_CONFIG[type];
  if (!config) return null;

  const displayUnit = unit ?? config.unit;
  const displayLabel = label ?? config.label;
  const displayValue = config.formatValue(value);
  const Icon = config.icon;
  
  const minVal = min ?? config.defaultMin;
  const maxVal = max ?? config.defaultMax;

  // Calculate percentage for gauge
  let percentage = 0;
  if (type !== 'occupancy' && typeof value === 'number') {
    percentage = Math.min(100, Math.max(0, ((value - minVal) / (maxVal - minVal)) * 100));
  } else if (type === 'occupancy') {
    percentage = value ? 100 : 0;
  }

  // SVG arc calculation
  const radius = 60;
  const strokeWidth = 8;
  const circumference = 2 * Math.PI * radius;
  const arc = circumference * 0.75; // 270 degrees
  const offset = arc - (arc * percentage) / 100;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-36 h-36">
        <svg className="w-full h-full transform -rotate-135" viewBox="0 0 140 140">
          {/* Background arc */}
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            stroke="#334155"
            strokeWidth={strokeWidth}
            strokeDasharray={`${arc} ${circumference}`}
            strokeLinecap="round"
          />
          {/* Value arc */}
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            stroke="url(#gaugeGradient)"
            strokeWidth={strokeWidth}
            strokeDasharray={`${arc} ${circumference}`}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
          <defs>
            <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" className={config.gradientFrom.replace('from-', 'stop-color: ')} style={{ stopColor: '#3b82f6' }} />
              <stop offset="100%" className={config.gradientTo.replace('to-', 'stop-color: ')} style={{ stopColor: '#f97316' }} />
            </linearGradient>
          </defs>
        </svg>
        
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <Icon className={`w-6 h-6 ${config.color} mb-1`} />
          <span className={`text-2xl font-bold ${config.color}`}>
            {displayValue}
          </span>
          {type !== 'power' && displayUnit && (
            <span className="text-xs text-slate-400">{displayUnit}</span>
          )}
        </div>
      </div>
      <span className="text-sm text-slate-300 mt-2">{displayLabel}</span>
    </div>
  );
};

export default Style03_Gauge;
